import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ReportsService } from '../../../reports.service';
import { ToastrService } from 'ngx-toastr';

export interface GlobalFilterColumnOption {
  key: string;
  label: string;
  tableName: string;
  columnName: string;
  // Dataset the column belongs to - used by "table" mode to pick a table before its column.
  datasetId?: string;
  tableLabel?: string;
}

export interface GlobalFilterTableOption {
  value: string;
  label: string;
}

export type GlobalFilterMode = 'global' | 'table';

export interface GlobalFilterCondition {
  id: string;
  tableKey: string | null;
  columnKey: string | null;
  selectedValues: string[];
  availableValues: string[];
  filteredValues: string[];
  valuesLoading: boolean;
  searchTerm: string;
  // Cached (not computed in the template) - columns already used by OTHER conditions are excluded.
  availableColumns: GlobalFilterColumnOption[];
}

export interface GlobalFilterSelection {
  columnKey: string;
  tableName: string;
  columnName: string;
  selectedValues: string[];
}

let conditionSequence = 0;

/**
 * "Manage global filter" popup body - a chip per condition (column + its selected values), with the
 * active condition's column picker + distinct-value multiselect (via getValuesByColumnNames) below.
 * In "table" mode ("Manage table filter") the condition asks for the table first and then only offers
 * that table's columns.
 */
@Component({
  selector: 'app-global-filter-modal',
  standalone: false,
  templateUrl: './global-filter-modal.component.html',
  styleUrl: './global-filter-modal.component.scss'
})
export class GlobalFilterModalComponent implements OnChanges {
  @Input() columnOptions: GlobalFilterColumnOption[] = [];
  @Input() mode: GlobalFilterMode = 'global';
  @Output() cancelled = new EventEmitter<void>();
  @Output() conditionsSaved = new EventEmitter<GlobalFilterSelection[]>();

  conditions: GlobalFilterCondition[] = [];
  activeConditionId: string | null = null;
  // Cached (not a getter) so the table mat-select keeps a stable option identity.
  tableOptions: GlobalFilterTableOption[] = [];

  constructor(private reportsService: ReportsService, private toastr: ToastrService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['columnOptions'] || changes['mode']) {
      this.tableOptions = this.buildTableOptions();
      this.refreshAvailableColumns();
    }
  }

  private buildTableOptions(): GlobalFilterTableOption[] {
    const tables = new Map<string, string>();
    this.columnOptions.forEach(option => {
      const value = option.datasetId ?? option.tableName;
      if (value && !tables.has(value)) {
        tables.set(value, option.tableLabel || option.tableName || value);
      }
    });
    return Array.from(tables, ([value, label]) => ({ value, label }));
  }

  private getTableKey(option: GlobalFilterColumnOption | undefined): string | null {
    return option ? option.datasetId ?? option.tableName ?? null : null;
  }

  // app-modal keeps this component mounted between opens - the host calls this before showing again.
  // Defaults to one empty condition so the column dropdown + values section is visible immediately,
  // instead of requiring an "Add condition" click first.
  resetState(initialSelections: GlobalFilterSelection[] = []): void {
    this.conditions = initialSelections.length
      ? initialSelections.map(selection => {
        const condition = this.createCondition();
        condition.columnKey = selection.columnKey;
        condition.tableKey = this.getTableKey(this.columnOptions.find(option => option.key === selection.columnKey));
        condition.selectedValues = [...selection.selectedValues];
        return condition;
      })
      : [this.createCondition()];
    this.activeConditionId = this.conditions[0]?.id || null;
    this.refreshAvailableColumns();
    this.conditions.forEach(condition => {
      if (condition.columnKey) {
        this.loadColumnValues(condition, condition.columnKey, true);
      }
    });
  }

  get canApply(): boolean {
    return this.conditions.some(condition =>
      !!condition.columnKey &&
      this.columnOptions.some(option => option.key === condition.columnKey) &&
      condition.selectedValues.length > 0
    );
  }

  get activeCondition(): GlobalFilterCondition | undefined {
    return this.conditions.find(condition => condition.id === this.activeConditionId);
  }

  // Columns already used by OTHER conditions can't be picked again ("not same" column requirement).
  // Recomputed for every condition whenever membership/selection changes - never call from the template.
  private refreshAvailableColumns(): void {
    this.conditions.forEach(condition => {
      const usedKeys = new Set(
        this.conditions.filter(other => other !== condition && other.columnKey).map(other => other.columnKey)
      );
      condition.availableColumns = this.columnOptions.filter(option =>
        !usedKeys.has(option.key) &&
        (this.mode !== 'table' || (!!condition.tableKey && this.getTableKey(option) === condition.tableKey))
      );
    });
  }

  getColumnLabel(condition: GlobalFilterCondition): string {
    const option = this.columnOptions.find(item => item.key === condition.columnKey);
    return this.mode === 'table' && option?.tableLabel ? `${option.tableLabel} - ${option.label}` : option?.label;
  }

  onTableSelected(condition: GlobalFilterCondition, tableKey: string): void {
    condition.tableKey = tableKey;
    condition.columnKey = null;
    condition.selectedValues = [];
    condition.availableValues = [];
    condition.filteredValues = [];
    condition.valuesLoading = false;
    condition.searchTerm = '';
    this.refreshAvailableColumns();
  }

  addCondition(): void {
    const condition = this.createCondition();
    this.conditions = [...this.conditions, condition];
    this.activeConditionId = condition.id;
    this.refreshAvailableColumns();
  }

  private createCondition(): GlobalFilterCondition {
    conditionSequence += 1;
    return {
      id: `condition-${conditionSequence}`,
      tableKey: null,
      columnKey: null,
      selectedValues: [],
      availableValues: [],
      filteredValues: [],
      valuesLoading: false,
      searchTerm: '',
      availableColumns: []
    };
  }

  selectCondition(condition: GlobalFilterCondition): void {
    this.activeConditionId = condition.id;
  }

  removeCondition(condition: GlobalFilterCondition): void {
    this.conditions = this.conditions.filter(item => item.id !== condition.id);
    if (this.activeConditionId === condition.id) {
      this.activeConditionId = this.conditions[0]?.id || null;
    }
    this.refreshAvailableColumns();
  }

  onColumnSelected(condition: GlobalFilterCondition, key: string): void {
    condition.selectedValues = [];
    this.loadColumnValues(condition, key);
    this.refreshAvailableColumns();
  }

  private loadColumnValues(condition: GlobalFilterCondition, key: string, preserveSelection = false): void {
    condition.columnKey = key;
    if (!preserveSelection) {
      condition.selectedValues = [];
    }
    condition.availableValues = [];
    condition.filteredValues = [];
    condition.searchTerm = '';
    const option = this.columnOptions.find(item => item.key === key);
    if (!option) {
      condition.valuesLoading = false;
      return;
    }
    condition.valuesLoading = true;
    this.reportsService.getValuesByColumnNames({
      tableName: option.tableName,
      columnNames: [option.columnName],
      distinct: true
    }).subscribe({
      next: (response: any) => {
        if (condition.columnKey !== key) { return; }
        condition.valuesLoading = false;
        const values = Array.isArray(response) ? response : (response?.data || response?.values || []);
        condition.availableValues = values
          .map((item: unknown) => this.getColumnValue(item, option))
          .filter((value: string | null): value is string => value !== null);
        this.updateFilteredValues(condition);
      },
      error: () => {
        if (condition.columnKey !== key) { return; }
        condition.valuesLoading = false;
        condition.availableValues = [];
        condition.filteredValues = [];
      }
    });
  }

  onSearchChanged(condition: GlobalFilterCondition, searchTerm: string): void {
    condition.searchTerm = searchTerm;
    this.updateFilteredValues(condition);
  }

  trackValue(_index: number, value: string): string {
    return value;
  }

  private updateFilteredValues(condition: GlobalFilterCondition): void {
    const term = (condition.searchTerm || '').trim().toLowerCase();
    condition.filteredValues = term
      ? condition.availableValues.filter(value => value.toLowerCase().includes(term))
      : condition.availableValues;
  }

  private getColumnValue(item: unknown, option: GlobalFilterColumnOption): string | null {
    if (item === null || item === undefined) { return null; }
    if (typeof item !== 'object') { return String(item); }

    const record = item as Record<string, unknown>;
    const keys = Object.keys(record);
    const columnKeys = [option.columnName, option.label, option.key.split('.').pop()]
      .filter((key): key is string => !!key)
      .map(key => this.normalizeColumnKey(key));
    const matchingKey = keys.find(key => columnKeys.includes(this.normalizeColumnKey(key)));
    const value = matchingKey ? record[matchingKey] : keys.length === 1 ? record[keys[0]] : undefined;
    return value === null || value === undefined ? null : String(value);
  }

  private normalizeColumnKey(key: string): string {
    return key.replace(/[^a-z0-9]/gi, '').toLowerCase();
  }

  isValueSelected(condition: GlobalFilterCondition, value: string): boolean {
    return condition.selectedValues.includes(value);
  }

  toggleValue(condition: GlobalFilterCondition, value: string): void {
    const idx = condition.selectedValues.indexOf(value);
    if (idx >= 0) { condition.selectedValues.splice(idx, 1); } else { condition.selectedValues.push(value); }
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onApply(): void {
    const selectedConditions = this.conditions.flatMap(condition => {
      const column = this.columnOptions.find(option => option.key === condition.columnKey);
      if (!column || !condition.columnKey || !condition.selectedValues.length) {
        return [];
      }
      return [{
        columnKey: condition.columnKey,
        tableName: column.tableName,
        columnName: column.columnName,
        selectedValues: [...condition.selectedValues]
      }];
    });
    if (!selectedConditions.length) {
      return;
    }

    this.conditionsSaved.emit(selectedConditions);
    this.toastr.success(this.mode === 'table' ? 'Table filter has been added successfully' : 'Global Filters are added successfully', '', {
      timeOut: 3000,
      positionClass: 'toast-top-center'
    });
  }
}
